/**
 * The broadcast cut between scenes (`scene-transition`): the stage dips to black under a flicker of
 * signal static, a title card names the incoming scene, and the new scene fades up. `sceneCutPlan.ts`
 * decides whether a change cuts; this runs it.
 *
 * `useSceneCut` lags the scene the stage renders (`displayed`) behind the scene it should show: the
 * outgoing scene stays mounted until the overlay is black, so the swap (COMBAT.app remounts per
 * encounter) happens out of sight. The card then holds until the incoming scene reports it has drawn
 * (`markReady`: the combat board's first frame, the backdrop's first frame) and at least
 * `CARD_MIN_MS` has passed, or until `READY_CEILING_MS`, whichever comes first. A narrative scene
 * with no backdrop is ready at the swap. Under reduced motion the swap is immediate and the overlay
 * only fades off black.
 *
 * The overlay covers the stage element and blocks the stage's input while it is opaque; rail pages
 * and COMMS stay above it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TableScene } from "@lancer-vtt/domain";
import { listEncounters } from "../../../lib/api";
import { prefersReducedMotion } from "../../../lib/prefersReducedMotion";
import {
  CARD_MIN_MS,
  cutCard,
  DIP_MS,
  READY_CEILING_MS,
  REDUCED_FADE_MS,
  RISE_MS,
  sceneCut,
  sceneKey,
  type CutCard,
  type CutKind,
} from "./sceneCutPlan";
import "./sceneCut.css";

type Phase = "dip" | "hold" | "rise";
export type CutState = { kind: CutKind; phase: Phase } | null;

export type SceneCutHandle = {
  /** The scene the stage renders: the outgoing one until the overlay is black. */
  displayed: TableScene;
  /** The running cut, or null. */
  cut: CutState;
  /** The incoming scene's title card. */
  card: CutCard;
  /** The incoming scene has drawn its first frame. Stable; ignored outside a cut's hold. */
  markReady: () => void;
};

/**
 * `shown` is the scene this client should show; `live` is false until the game session's welcome,
 * so the scene a page load lands on never cuts; `world` is the pinned backdrop's name, or null.
 */
export function useSceneCut(args: { shown: TableScene; live: boolean; world: string | null }): SceneCutHandle {
  const { shown, live, world } = args;
  const [displayed, setDisplayed] = useState(shown);
  const [cut, setCut] = useState<CutState>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const m = useRef({
    armed: false,
    displayed: shown,
    phase: null as Phase | null,
    kind: "broadcast" as CutKind,
    ready: false,
    holdSince: 0,
    hasBackdrop: world !== null,
    timers: [] as number[],
  }).current;
  m.hasBackdrop = world !== null;

  const ops = useMemo(() => {
    const clear = () => {
      for (const t of m.timers) window.clearTimeout(t);
      m.timers = [];
    };
    const later = (ms: number, fn: () => void) => m.timers.push(window.setTimeout(fn, ms));
    const phase = (kind: CutKind, next: Phase | null) => {
      m.phase = next;
      m.kind = kind;
      setCut(next === null ? null : { kind, phase: next });
    };
    const swap = (scene: TableScene) => {
      m.displayed = scene;
      setDisplayed(scene);
    };
    const rise = (kind: CutKind) => {
      clear();
      phase(kind, "rise");
      later(kind === "fade" ? REDUCED_FADE_MS : RISE_MS, () => phase(kind, null));
    };
    const riseIfReady = () => {
      if (m.phase === "hold" && m.ready && performance.now() - m.holdSince >= CARD_MIN_MS) rise("broadcast");
    };
    return { clear, later, phase, swap, rise, riseIfReady };
  }, [m]);

  const key = sceneKey(shown);
  useEffect(() => {
    const kind = sceneCut({ prev: m.armed ? m.displayed : undefined, next: shown, reducedMotion: prefersReducedMotion() });
    if (live) m.armed = true;
    ops.clear();
    if (kind === null) {
      ops.swap(shown);
      // Back to the scene the stage still shows, mid-dip: lift the overlay off it.
      if (m.phase !== null) ops.rise(m.kind);
      return;
    }
    if (shown.kind === "encounter") {
      void listEncounters().then((listed) => {
        if (listed.ok) setNames(new Map(listed.value.map((e) => [e.id, e.name])));
      });
    }
    if (kind === "fade") {
      ops.swap(shown);
      ops.phase(kind, "hold");
      // Two frames on black, so the fade has a painted start.
      ops.later(32, () => ops.rise(kind));
      return;
    }
    ops.phase(kind, "dip");
    ops.later(DIP_MS, () => {
      ops.swap(shown);
      m.ready = shown.kind === "narrative" && !m.hasBackdrop;
      m.holdSince = performance.now();
      ops.phase(kind, "hold");
      ops.later(CARD_MIN_MS, ops.riseIfReady);
      ops.later(READY_CEILING_MS, () => ops.rise(kind));
    });
    // Keyed on the scene: a cut starts only when the shown scene changes (or the session goes live).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, live]);

  useEffect(() => ops.clear, [ops]);

  const markReady = useCallback(() => {
    if (m.phase !== "hold") return;
    m.ready = true;
    ops.riseIfReady();
  }, [m, ops]);

  const card = cutCard({ scene: displayed, nameOf: (id) => names.get(id), world });
  return { displayed, cut, card, markReady };
}

/** The overlay: black, static while it dips, the card while it holds. Rendered inside the stage. */
export function SceneCutOverlay({ cut, card }: { cut: CutState; card: CutCard }) {
  if (cut === null) return null;
  const showCard = cut.kind === "broadcast" && cut.phase !== "dip";
  return (
    <div className={`scene-cut scene-cut--${cut.kind} scene-cut--${cut.phase}`} data-phase={cut.phase} data-kind={cut.kind}>
      {cut.kind === "broadcast" && <div className="scene-cut-static" aria-hidden="true" />}
      {showCard && (
        <div className="scene-cut-card" role="status" aria-label="Scene">
          <div className="scene-cut-kicker">
            <span>GMS//OS</span>
            <span className="scene-cut-kicker-fill" />
            <span>NOW SHOWING</span>
          </div>
          <div className="scene-cut-rule" />
          <div className="scene-cut-title">{card.title}</div>
          {card.world !== null && <div className="scene-cut-world">{card.world}</div>}
        </div>
      )}
    </div>
  );
}
